import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AppDialog } from '../../../components/common/CustomMdal';
import {
  attachPlayersToLeague,
  checkDuplicatesInLeague,
  fetchDivisions,
  fetchTeamPlayers,
  updateDivision,
} from '../api/myTeamApi';
import type { IDivisionResponse, IDivisionsPayload, IMember, IMyTeam } from '../types/myTeam';
import { getTeamsFromDivisionNode } from '../../leaguesHome/teams/divisionTeamUtils';
import DivisionSelect from './DivisionSelect';

/** Snapshot ids currently on this division node (PUT membership must list all that should remain). */
function collectTeamSnapshotIdsFromDivisionNode(division: IDivisionResponse | undefined | null): string[] {
  if (!division) return [];
  const rows = getTeamsFromDivisionNode(division) as Array<{ teamSnapshotId?: string; snapshotId?: string }>;
  const ids = rows
    .map((r) => (r.teamSnapshotId ?? r.snapshotId ?? '').trim())
    .filter(Boolean);
  return Array.from(new Set(ids));
}

interface SelectDivisionGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  teamId: string;
  leagueId: string;
  team: IMyTeam;
  onSuccess?: () => void;
}

/** Recursively find teamSnapshotId for a teamId in divisions */
function findTeamSnapshotIdInDivisions(
  divisions: IDivisionResponse[] | undefined,
  teamId: string
): string | null {
  if (!divisions?.length) return null;
  for (const div of divisions) {
    const teams = div.teams as Array<{
      teamSnapshotId?: string;
      teamId?: string;
      team?: { teamId?: string };
    }> | undefined;
    if (Array.isArray(teams)) {
      const found = teams.find(
        (t) =>
          t.team?.teamId === teamId || (t as { teamId?: string }).teamId === teamId
      );
      if (found?.teamSnapshotId) return found.teamSnapshotId;
    }
    if (div.childDivisions?.length) {
      const inChild = findTeamSnapshotIdInDivisions(div.childDivisions, teamId);
      if (inChild) return inChild;
    }
  }
  return null;
}

function SelectDivisionGroupModal({
  open,
  onOpenChange,
  tournamentId,
  teamId,
  leagueId,
  team,
  onSuccess,
}: SelectDivisionGroupModalProps) {
  const [divisionId, setDivisionId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [divisions, setDivisions] = useState<IDivisionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !tournamentId) return;
    setLoading(true);
    fetchDivisions(tournamentId)
      .then((payload) => {
        const list = Array.isArray(payload)
          ? payload
          : payload?.divisionResponseList ??
            (payload as { divisionResponses?: IDivisionResponse[] })?.divisionResponses ??
            [];
        setDivisions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setDivisions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, tournamentId]);

  const selectedDivision = divisions.find((d) => d.divisionId === divisionId);
  const childDivisions = selectedDivision?.childDivisions ?? [];
  const hasGroups = childDivisions.length > 0;
  const targetDivisionId = hasGroups ? groupId : divisionId;
  const targetDivision = hasGroups
    ? childDivisions.find((g) => g.divisionId === groupId)
    : selectedDivision;
  const divisionName = targetDivision?.divisionName ?? '';

  const isFormValid = divisionId && (!hasGroups || groupId);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!tournamentId || !teamId || !leagueId || !targetDivisionId || !divisionName) return;
      setSubmitting(true);
      try {
        const existingSnaps = collectTeamSnapshotIdsFromDivisionNode(targetDivision ?? undefined);
        const mySnap = (team.teamSnapshotId ?? '').trim();
        const teamCode = (team.teamCode || team.teamName?.slice(0, 10) || 'TM').trim();

        // PUT /division API notes: existing members stay via `teamSnapShotIdList`; brand-new via
        // `newTeams.teamSnapshotPutRequestList` with only teamName, teamCode, tournamentId, divisionId.
        await updateDivision({
          divisionId: targetDivisionId,
          name: divisionName,
          teamSnapShotIdList: mySnap
            ? Array.from(new Set([...existingSnaps, mySnap]))
            : existingSnaps,
          newTeams: mySnap
            ? { teamSnapshotPutRequestList: [] }
            : {
                teamSnapshotPutRequestList: [
                  {
                    teamName: team.teamName,
                    teamCode,
                    tournamentId,
                    divisionId: targetDivisionId,
                    ...(team.description ? { description: team.description } : {}),
                    ...(team.teamSnapshotId ? { teamSnapshotId: team.teamSnapshotId } : {}),
                    ...(team.teamId ? { teamId: team.teamId } : {}),
                    ...(team.groundId ? { groundId: team.groundId } : {}),
                    ...(leagueId ? { leagueId } : {}),
                    ...(team.captainPlayerId ? { captainPlayerId: team.captainPlayerId } : {}),
                    ...(team.viceCaptainPlayerId ? { viceCaptainPlayerId: team.viceCaptainPlayerId } : {}),
                    ...(team.wkPlayerId ? { wkPlayerId: team.wkPlayerId } : {}),
                  },
                ],
              },
        });

        // Step 5: Fetch divisions again to find new teamSnapshotId (retry with short delay if not found)
        let newTeamSnapshotId: string | null = null;
        for (let attempt = 0; attempt < 3 && !newTeamSnapshotId; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 500));
          const divisionsPayload: IDivisionsPayload = await fetchDivisions(tournamentId);
          const divList =
            Array.isArray(divisionsPayload)
              ? divisionsPayload
              : divisionsPayload?.divisionResponseList ??
                (divisionsPayload as { divisionResponseList?: IDivisionResponse[] })?.divisionResponseList ??
                [];
          newTeamSnapshotId = findTeamSnapshotIdInDivisions(
            Array.isArray(divList) ? divList : [],
            teamId
          );
        }

        if (!newTeamSnapshotId) {
          toast.success('Team joined division. You may need to add players to the tournament separately.');
          setDivisionId('');
          setGroupId('');
          onOpenChange(false);
          setTimeout(() => onSuccess?.(), 500);
          return;
        }

        // Step 6: Fetch team players
        const members: IMember[] = await fetchTeamPlayers(teamId);
        const playerIds = members.map((m) => m.playerId);
        if (playerIds.length === 0) {
          toast.success('Team joined tournament successfully.');
          setDivisionId('');
          setGroupId('');
          onOpenChange(false);
          setTimeout(() => onSuccess?.(), 500);
          return;
        }

        // Step 7: Check duplicates in league
        const dupResult = await checkDuplicatesInLeague({
          leagueId,
          teamId: newTeamSnapshotId,
          players: members.map((m) => ({
            playerId: m.playerId,
            playerFirstName: m.playerFirstName,
            playerLastName: m.playerLastName,
          })),
          playerIds,
        });

        // Step 8: Filter non-duplicates
        const duplicateKeys = new Set(
          (dupResult.duplicateInLeague ?? []).map((d) =>
            `${(d.firstName ?? '').toLowerCase().trim()}_${(d.lastName ?? '').toLowerCase().trim()}`
          )
        );
        const nonDuplicateMembers = members.filter((m) => {
          const fn = (m.playerFirstName ?? '').toLowerCase().trim();
          const ln = (m.playerLastName ?? '').toLowerCase().trim();
          const key = `${fn}_${ln}`;
          return !duplicateKeys.has(key);
        });
        const nonDuplicatePlayerIds = nonDuplicateMembers.map((m) => m.playerId);

        // Step 9: Attach players to league
        if (nonDuplicatePlayerIds.length > 0) {
          await attachPlayersToLeague({
            leagueId,
            teamId: newTeamSnapshotId,
            playerIds: nonDuplicatePlayerIds,
          });
        }

        toast.success('Team joined tournament successfully.');
        setDivisionId('');
        setGroupId('');
        onOpenChange(false);
        setTimeout(() => onSuccess?.(), 500);
      } catch (err: unknown) {
        console.error('Failed to join tournament', err);
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        toast.error(msg || 'Failed to join tournament. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [tournamentId, teamId, leagueId, targetDivisionId, divisionName, team, onOpenChange, onSuccess]
  );

  return (
    <AppDialog open={open} onOpenChange={onOpenChange} title="Select Division and Group" size="md" className="select-division-dialog">
      <form onSubmit={handleSubmit} className="select-division-group-form">
        {loading ? (
          <p className="select-division-loading">Loading divisions...</p>
        ) : (
          <>
            <div className="select-division-field">
              <DivisionSelect
                value={divisionId}
                onChange={(v) => {
                  setDivisionId(v);
                  setGroupId('');
                }}
                options={divisions.map((d) => ({ value: d.divisionId, label: d.divisionName }))}
                placeholder="Select Division"
              />
            </div>
            <div className="select-division-field">
              <DivisionSelect
                value={groupId}
                onChange={setGroupId}
                options={childDivisions.map((g) => ({ value: g.divisionId, label: g.divisionName }))}
                placeholder="Select Group"
                disabled={!hasGroups}
              />
            </div>
            {divisions.length === 0 && !loading && (
              <p className="select-division-no-divisions">No divisions available.</p>
            )}
          </>
        )}
        <div className="select-division-footer">
          <button
            type="submit"
            className={`select-division-submit ${isFormValid ? 'active' : ''}`}
            disabled={!isFormValid || loading || submitting}
          >
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </div>
      </form>
    </AppDialog>
  );
}

export default SelectDivisionGroupModal;
